// quickActionCalculations.ts - Utility functions for Quick Action calculations
//
// Field names are never read directly here: they come from the configured
// analytics schema (src/modules/ai/utils/analyticsSchema.ts), so the same
// calculations work on a retail lease plan in square feet, the same plan in
// square metres, or a corporate occupancy plan.

import {
  AnalyticsFieldRole,
  AnalyticsSchemaConfig,
  WingAnalysisColumn,
  WingAnalysisConfig,
} from "../../../../../types/analytics";
import { PlanInfo, SpaceInfo, TenantInfo } from "../../../../../types/plan";
import {
  columnRole,
  defaultWingColumns,
  describeRole,
  fieldNumber,
  fieldText,
  OccupantMetrics,
  parseNumber,
  readOccupantMetrics,
  ResolvedAnalyticsSchema,
  resolveAnalyticsSchema,
  resolveOccupantTab,
  resolveWingConfig,
  unresolvedRoles,
} from "../../../utils/analyticsSchema";
import { TableColumnType, TableData } from "../../../types/table";

// Deal stages that qualify for Future Tenant calculation
const QUALIFYING_DEAL_STAGES = [
  "executed",
  "signed, fixed rent commence",
  "signed, variable rent commence",
  "open",
  "mutually approved",
];

/**
 * Per-customer analytics settings threaded from the app config into every
 * calculation. Omitted → the built-in retail defaults.
 */
export interface QuickActionOptions {
  analyticsSchema?: AnalyticsSchemaConfig;
  wingAnalysisConfig?: WingAnalysisConfig;
  /**
   * `PlanInfo.planType` of the loaded plan, used to pick the per-plan-type
   * override (a lease plan and a corporate plan need different mappings).
   */
  planType?: string;
}

/**
 * Parse a date string.
 *
 * Handles "M/D/YYYY", "D-M-YYYY", ISO and anything `Date` accepts. Day/month
 * order is decided per `dateOrder`; on "auto" a component above 12 settles it,
 * and otherwise the separator does — slashes read US ("3/1/2021" = 1 March),
 * dashes read day-first ("30-4-2027" = 30 April), which is what the datasets we
 * have seen use. Getting this wrong is not cosmetic: these dates drive the
 * remaining-lease value in the Opportunity Cost.
 */
function parseDate(
  dateStr: string | undefined | null,
  dateOrder: "auto" | "MDY" | "DMY" = "auto"
): Date | null {
  if (!dateStr) return null;
  const text = String(dateStr).trim();
  if (!text) return null;

  const parts = text.match(/^(\d{1,2})([/-])(\d{1,2})\2(\d{4})$/);
  if (parts) {
    const first = parseInt(parts[1], 10);
    const separator = parts[2];
    const second = parseInt(parts[3], 10);
    const year = parseInt(parts[4], 10);

    let dayFirst: boolean;
    if (dateOrder === "DMY") dayFirst = true;
    else if (dateOrder === "MDY") dayFirst = false;
    else if (first > 12) dayFirst = true;
    else if (second > 12) dayFirst = false;
    else dayFirst = separator === "-";

    const day = dayFirst ? first : second;
    const month = dayFirst ? second : first;
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  }

  // Try standard Date parsing (ISO format, etc.)
  const date = new Date(text);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a date as YYYY-MM-DD in local time.
 *
 * `toISOString()` converts to UTC first, so a date built from local components
 * (as parseDate does) came out one day earlier for every user east of UTC — a
 * lease ending 30 April was reported as the 29th.
 */
function toIsoDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Calculate days between two dates
 */
function daysBetween(date1: Date, date2: Date): number {
  const diffTime = date2.getTime() - date1.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Get the occupant record of a space, through the configured caption patterns.
 * Which detail tab holds it is customer-specific ("Tenant Information" on a
 * lease plan, "Employee Information" on a corporate plan), so the caption is
 * matched against the schema rather than against a literal.
 */
function getTenantInfo(
  space: SpaceInfo,
  tabType: "current" | "future",
  schema: ResolvedAnalyticsSchema
): TenantInfo | null {
  return resolveOccupantTab(space, schema, tabType).detail;
}

/**
 * Build the "your mapping did not match this dataset" warning. Returning a table
 * of zeros is what let the misconfiguration go unnoticed, so unresolved roles and
 * guessed tabs are surfaced with the keys that were tried.
 */
function buildSchemaWarning(
  spaces: SpaceInfo[],
  details: (TenantInfo | null)[],
  requiredRoles: AnalyticsFieldRole[],
  schema: ResolvedAnalyticsSchema
): string {
  const notes: string[] = [];

  const withoutRecord = spaces.filter((_, i) => details[i] === null).length;
  if (withoutRecord === spaces.length) {
    notes.push(
      `No occupant record found in any of the ${spaces.length} selected space(s): ` +
        `no detail tab matched ${schema.tenantTabMatch
          .map((p) => `"${p}"`)
          .join(" / ")}. Configure analyticsSchema.tenantTabMatch for this dataset.`
    );
  } else if (withoutRecord > 0) {
    notes.push(`${withoutRecord} of ${spaces.length} space(s) have no occupant record.`);
  }

  const guessed = spaces.filter(
    (space) => resolveOccupantTab(space, schema, "current").usedFallback
  ).length;
  if (guessed > 0) {
    notes.push(
      `${guessed} space(s) fell back to their first detail tab — the field mapping is a guess.`
    );
  }

  const missing = unresolvedRoles(details, requiredRoles, schema);
  if (missing.length > 0) {
    notes.push(
      `Not found in this dataset: ${missing
        .map((role) => describeRole(role, schema))
        .join("; ")}.`
    );
  }

  return notes.join(" ");
}

/** Attach a warning to a table without clobbering one already set. */
function withWarning(table: TableData, warning: string): TableData {
  if (!warning) return table;
  table.warning = table.warning ? `${table.warning} ${warning}` : warning;
  return table;
}

/**
 * Check if a deal stage qualifies for Future Tenant calculation
 */
function isQualifyingDealStage(dealStage: string | undefined | null): boolean {
  if (!dealStage) return false;
  const normalized = dealStage.toLowerCase().trim();
  return QUALIFYING_DEAL_STAGES.some((stage) => normalized.includes(stage));
}

/**
 * Normalize a space identifier for tolerant comparison. CADViewer SpaceIDs use
 * "_" where the plan JSON may use "." or " " (e.g. "1310_A" vs "1310.A" vs
 * "1310 A"), so we collapse all of those to a single space + lowercase.
 */
export function normalizeSpaceKey(value: string | undefined | null): string {
  return (value || "")
    .replace(/[._]/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();
}

/** True if `space` (by number or header) matches the normalized selected id. */
function spaceMatchesKey(space: SpaceInfo, key: string): boolean {
  return (
    key === normalizeSpaceKey(space.spaceNumber) ||
    key === normalizeSpaceKey(space.spaceHeader)
  );
}

/** One selected id that resolved to a space through its trailing number
 *  (e.g. the alternate-plan polygon "ALT-STD-5503" → space "5503"). */
export interface AliasResolution {
  selectedId: string;
  resolvedTo: string;
  /** True when the base space was itself also selected (duplicate). */
  alreadySelected: boolean;
}

export interface SelectionResolution {
  /** Unique matched spaces, in selection order. */
  matched: SpaceInfo[];
  /** Selected ids with no data record at all. */
  unmatched: string[];
  /** Ids that only matched via their trailing number (alternate polygons). */
  aliases: AliasResolution[];
}

/**
 * Resolve selected CAD ids to plan-data spaces. Matches by spaceNumber or
 * spaceHeader first; ids that don't match directly (e.g. alternate-plan
 * polygons like "ALT-STD-5503") fall back to their trailing number token.
 * Duplicates (two ids resolving to the same space) are counted once.
 */
export function resolveSelectedSpaces(
  plansInfo: PlanInfo | undefined,
  selectedSpaceNumbers: string[]
): SelectionResolution {
  const spaces = plansInfo?.spaces || [];
  const matched: SpaceInfo[] = [];
  const matchedSet = new Set<SpaceInfo>();
  const unmatched: string[] = [];
  const aliases: AliasResolution[] = [];

  const directKeys = new Set(selectedSpaceNumbers.map(normalizeSpaceKey));

  for (const sel of selectedSpaceNumbers) {
    const key = normalizeSpaceKey(sel);
    let space = spaces.find((s) => spaceMatchesKey(s, key));

    if (!space) {
      // Alias fallback: "alt-std-5503" → trailing token "5503" (must contain a
      // digit and be ≥3 chars so suffixes like "1310 A" don't mis-match).
      const tokens = key.split(/[-\s]+/).filter(Boolean);
      const tail = tokens[tokens.length - 1];
      if (tokens.length > 1 && tail.length >= 3 && /\d/.test(tail)) {
        space = spaces.find((s) => spaceMatchesKey(s, tail));
        if (space) {
          aliases.push({
            selectedId: sel,
            resolvedTo: space.spaceNumber || tail,
            alreadySelected: directKeys.has(normalizeSpaceKey(space.spaceNumber)),
          });
        }
      }
    }

    if (!space) {
      unmatched.push(sel);
      continue;
    }
    if (!matchedSet.has(space)) {
      matchedSet.add(space);
      matched.push(space);
    }
  }

  return { matched, unmatched, aliases };
}

/**
 * Get spaces data from plansInfo based on selected space identifiers
 * Can match by spaceNumber OR spaceHeader (tenant name)
 */
export function getSelectedSpacesData(
  plansInfo: PlanInfo | undefined,
  selectedSpaceNumbers: string[]
): SpaceInfo[] {
  if (!plansInfo?.spaces || selectedSpaceNumbers.length === 0) return [];
  return resolveSelectedSpaces(plansInfo, selectedSpaceNumbers).matched;
}

/**
 * Return the selected identifiers that matched NO space in the plan JSON. These
 * are spaces the user selected on the CAD plan but which have no data entry, so
 * the analysis would otherwise silently ignore them.
 */
export function getUnmatchedSpaceIds(
  plansInfo: PlanInfo | undefined,
  selectedSpaceNumbers: string[]
): string[] {
  if (selectedSpaceNumbers.length === 0) return [];
  return resolveSelectedSpaces(plansInfo, selectedSpaceNumbers).unmatched;
}

/**
 * Calculate Financial Summary for selected spaces.
 *
 * Row keys stay stable (`gla_sf`, `sales_r12_usd`, …) because the table renderer
 * and the PDF infer cell formatting from them; only the *labels* follow the
 * customer's unit.
 */
export function calculateFinancialSummary(
  spaces: SpaceInfo[],
  options?: QuickActionOptions
): TableData | null {
  if (spaces.length === 0) return null;

  const schema = resolveAnalyticsSchema(options?.analyticsSchema, options?.planType);
  const details = spaces.map((space) => getTenantInfo(space, "current", schema));

  const data: Record<string, any>[] = [];
  let totalSalesR12 = 0;
  let totalRent = 0;
  let totalGLA = 0;
  let totalOccupancyCost = 0;
  let validOccupancyCount = 0;

  spaces.forEach((space, index) => {
    const currentTenant = details[index];
    const metrics = readOccupantMetrics(currentTenant, schema);

    totalSalesR12 += metrics.sales;
    totalRent += metrics.rent;
    totalGLA += metrics.area;
    if (metrics.occCostPct > 0) {
      totalOccupancyCost += metrics.occCostPct;
      validOccupancyCount++;
    }

    data.push({
      space_number: space.spaceNumber || "N/A",
      tenant_name:
        fieldText(currentTenant, "tenantName", schema) || space.spaceHeader || "Vacant",
      gla_sf: metrics.area,
      sales_r12_usd: metrics.sales,
      total_rent_usd: metrics.rent,
      rent_per_sf: round2(metrics.rentPerArea),
      occupancy_cost_percent: round2(metrics.occCostPct),
    });
  });

  const avgRentPSF = totalGLA > 0 ? totalRent / totalGLA : 0;
  const avgOccupancyCost =
    validOccupancyCount > 0 ? totalOccupancyCost / validOccupancyCount : 0;

  data.push({
    space_number: "TOTAL / AVERAGE",
    tenant_name: `${spaces.length} space(s)`,
    gla_sf: totalGLA,
    sales_r12_usd: totalSalesR12,
    total_rent_usd: totalRent,
    rent_per_sf: round2(avgRentPSF),
    occupancy_cost_percent: round2(avgOccupancyCost),
    _is_summary: true,
  });

  const area = schema.areaUnitLabel;
  return withWarning(
    {
      labels: {
        space_number: "Space #",
        tenant_name: "Tenant",
        gla_sf: `GLA (${area})`,
        sales_r12_usd: "Sales R12",
        total_rent_usd: "Total Rent",
        rent_per_sf: `Rent/${area}`,
        occupancy_cost_percent: "Occupancy Cost %",
      },
      data,
      columnTypes: {
        gla_sf: "area",
        sales_r12_usd: "money",
        total_rent_usd: "money",
        rent_per_sf: "money",
        occupancy_cost_percent: "percentage",
      },
      currency: schema.currencyLabel,
      areaUnit: schema.areaUnitLabel,
    },
    buildSchemaWarning(
      spaces,
      details,
      ["tenantName", "area", "sales", "rent"],
      schema
    )
  );
}

/**
 * Calculate Opportunity Cost for selected spaces
 */
export function calculateOpportunityCost(
  spaces: SpaceInfo[],
  options?: QuickActionOptions
): TableData | null {
  if (spaces.length === 0) return null;

  const schema = resolveAnalyticsSchema(options?.analyticsSchema, options?.planType);
  const details = spaces.map((space) => getTenantInfo(space, "current", schema));

  const today = new Date();
  const data: Record<string, any>[] = [];
  let totalOpportunityCost = 0;

  for (const space of spaces) {
    const currentTenant = getTenantInfo(space, "current", schema);
    const futureTenant = getTenantInfo(space, "future", schema);

    // Current Tenant calculation
    const currentTotalRent = fieldNumber(currentTenant, "rent", schema);
    const leaseEndDate = parseDate(fieldText(currentTenant, "leaseEnd", schema), schema.dateOrder);

    let currentTenantCharge = 0;
    let daysUntilExpiration = 0;

    if (leaseEndDate && leaseEndDate > today) {
      daysUntilExpiration = daysBetween(today, leaseEndDate);
      currentTenantCharge = (currentTotalRent * daysUntilExpiration) / 365;
    }

    // Future Tenant calculation (only if qualifying deal stage)
    // Future Tenant Charge = Total Rent × (Rent Expiration Date - Lease Start Date) / 365
    let futureTenantCharge = 0;
    let futureTenantName = "";
    let futureTotalRent = 0;
    let futureRentExpiration: Date | null = null;
    let futureLeaseStartDate: Date | null = null;
    let futureLeaseDays = 0;

    if (
      futureTenant &&
      isQualifyingDealStage(fieldText(futureTenant, "dealStage", schema))
    ) {
      futureTenantName =
        fieldText(futureTenant, "tenantName", schema) ||
        fieldText(futureTenant, "dealName", schema) ||
        "Future Tenant";
      futureTotalRent = fieldNumber(futureTenant, "rent", schema);
      futureRentExpiration = parseDate(
        fieldText(futureTenant, "rentExpiration", schema),
        schema.dateOrder
      );
      // The start date lives under one of several keys; the schema lists them.
      futureLeaseStartDate = parseDate(
        fieldText(futureTenant, "leaseStart", schema),
        schema.dateOrder
      );

      if (futureRentExpiration && futureLeaseStartDate && futureRentExpiration > futureLeaseStartDate) {
        futureLeaseDays = daysBetween(futureLeaseStartDate, futureRentExpiration);
        futureTenantCharge = (futureTotalRent * futureLeaseDays) / 365;
      }
    }

    const spaceOpportunityCost = currentTenantCharge + futureTenantCharge;
    totalOpportunityCost += spaceOpportunityCost;

    data.push({
      space_number: space.spaceNumber || "N/A",
      current_tenant:
        fieldText(currentTenant, "tenantName", schema) || space.spaceHeader || "Vacant",
      current_total_rent_usd: currentTotalRent,
      lease_end_date: leaseEndDate
        ? toIsoDay(leaseEndDate)
        : "N/A",
      days_until_expiration: daysUntilExpiration,
      current_tenant_charge_usd: currentTenantCharge,
      future_tenant: futureTenantName || "N/A",
      future_total_rent_usd: futureTotalRent || 0,
      future_lease_start: futureLeaseStartDate
        ? toIsoDay(futureLeaseStartDate)
        : "N/A",
      future_rent_expiration: futureRentExpiration
        ? toIsoDay(futureRentExpiration)
        : "N/A",
      future_lease_days: futureLeaseDays,
      future_tenant_charge_usd: futureTenantCharge,
      opportunity_cost_usd: spaceOpportunityCost,
    });
  }

  // Add total row
  data.push({
    space_number: "TOTAL",
    current_tenant: `${spaces.length} space(s)`,
    current_total_rent_usd: data.reduce(
      (sum, row) => sum + (row.current_total_rent_usd || 0),
      0
    ),
    lease_end_date: "-",
    days_until_expiration: "-",
    current_tenant_charge_usd: data.reduce(
      (sum, row) => sum + (row.current_tenant_charge_usd || 0),
      0
    ),
    future_tenant: "-",
    future_total_rent_usd: data.reduce(
      (sum, row) => sum + (row.future_total_rent_usd || 0),
      0
    ),
    future_lease_start: "-",
    future_rent_expiration: "-",
    future_lease_days: "-",
    future_tenant_charge_usd: data.reduce(
      (sum, row) => sum + (row.future_tenant_charge_usd || 0),
      0
    ),
    opportunity_cost_usd: totalOpportunityCost,
    _is_summary: true,
  });

  return {
    labels: {
      space_number: "Space #",
      current_tenant: "Current Tenant",
      current_total_rent_usd: "Current Rent",
      lease_end_date: "Lease End",
      days_until_expiration: "Days Left",
      current_tenant_charge_usd: "Current Charge",
      future_tenant: "Future Tenant",
      future_total_rent_usd: "Future Rent",
      future_lease_start: "Future Start",
      future_rent_expiration: "Future Exp.",
      future_lease_days: "Future Days",
      future_tenant_charge_usd: "Future Charge",
      opportunity_cost_usd: "Opportunity Cost",
    },
    data,
    columnTypes: {
      current_total_rent_usd: "money",
      lease_end_date: "date",
      days_until_expiration: "number",
      current_tenant_charge_usd: "money",
      future_total_rent_usd: "money",
      future_lease_start: "date",
      future_rent_expiration: "date",
      future_lease_days: "number",
      future_tenant_charge_usd: "money",
      opportunity_cost_usd: "money",
    },
    currency: schema.currencyLabel,
    areaUnit: schema.areaUnitLabel,
    warning:
      buildSchemaWarning(spaces, details, ["tenantName", "rent", "leaseEnd"], schema) ||
      undefined,
  };
}

/**
 * Round to 2 decimals for clean deal-sheet figures.
 */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Weighted mean, falling back to a plain mean when every weight is 0. */
function weightedMean(values: number[], weights: number[]): number {
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  if (totalWeight <= 0) {
    return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
  }
  return (
    values.reduce((sum, value, i) => sum + value * (weights[i] || 0), 0) / totalWeight
  );
}

/** Value of one configured column for one space. */
function wingCellValue(
  column: WingAnalysisColumn,
  space: SpaceInfo,
  detail: TenantInfo | null,
  metrics: OccupantMetrics,
  schema: ResolvedAnalyticsSchema
): string | number {
  if (column.source === "spaceNumber") return space.spaceNumber || "N/A";
  if (column.source === "spaceHeader") return space.spaceHeader || "";

  const role = columnRole(column);
  if (!role) return "";

  switch (role) {
    case "tenantName":
      return fieldText(detail, "tenantName", schema) || space.spaceHeader || "Vacant";
    case "area":
      return metrics.area;
    case "sales":
      return metrics.sales;
    case "salesPerArea":
      return round2(metrics.salesPerArea);
    case "rent":
      return metrics.rent;
    case "rentPerArea":
      return round2(metrics.rentPerArea);
    case "occCostPerArea":
      return round2(metrics.occCostPerArea);
    case "occCostPct":
      return round2(metrics.occCostPct);
    default: {
      // Dates, categories, deal stages and any other free-form role.
      const text = fieldText(detail, role, schema);
      if (column.type && column.type !== "text" && column.type !== "date") {
        return round2(parseNumber(text));
      }
      return text;
    }
  }
}

/** Summary-row cell for one configured column. */
function wingSummaryValue(
  column: WingAnalysisColumn,
  rows: Record<string, any>[],
  metrics: OccupantMetrics[]
): string | number {
  const aggregate = column.aggregate ?? "none";
  if (aggregate === "none") return "";

  const values = rows.map((row) => parseNumber(row[column.key]));
  switch (aggregate) {
    case "sum":
      return round2(values.reduce((sum, v) => sum + v, 0));
    case "avg":
      return round2(
        values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0
      );
    case "weightedByArea":
      return round2(weightedMean(values, metrics.map((m) => m.area)));
    case "weightedBySales":
      return round2(weightedMean(values, metrics.map((m) => m.sales)));
    default:
      return "";
  }
}

/**
 * Calculate the Wing Analysis for selected spaces: a comparison of similar-sized
 * occupants in one wing, plus a summary row.
 *
 * Columns are configurable because the retail default (Sales PSF, Occ Cost PSF,
 * Occ Cost %) is meaningless on a dataset without sales — a corporate or
 * healthcare plan configures its own (Room #, Occupant, Area, Department) rather
 * than getting a table of zeros.
 *
 * Summary aggregates: per-area figures roll up area-weighted and a percentage of
 * sales rolls up sales-weighted, which is arithmetically the same as total cost ÷
 * total sales.
 */
export function calculateWingAnalysis(
  spaces: SpaceInfo[],
  options?: QuickActionOptions
): TableData | null {
  if (spaces.length === 0) return null;

  const schema = resolveAnalyticsSchema(options?.analyticsSchema, options?.planType);
  const wing = resolveWingConfig(options?.wingAnalysisConfig, options?.planType);
  const columns =
    wing.columns && wing.columns.length > 0 ? wing.columns : defaultWingColumns(schema);

  const details = spaces.map((space) => getTenantInfo(space, "current", schema));
  const metrics = details.map((detail) => readOccupantMetrics(detail, schema));

  const data: Record<string, any>[] = spaces.map((space, index) => {
    const row: Record<string, any> = {};
    for (const column of columns) {
      row[column.key] = wingCellValue(
        column,
        space,
        details[index],
        metrics[index],
        schema
      );
    }
    return row;
  });

  const summary: Record<string, any> = { _is_summary: true };
  for (const column of columns) {
    summary[column.key] = wingSummaryValue(column, data, metrics);
  }
  // As on the deal sheet: the first column labels the row, the second lists the
  // spaces it covers (only when that column carries no aggregate of its own).
  const [first, second] = columns;
  if (first) summary[first.key] = wing.summaryLabel ?? "WING AVERAGE";
  if (second && (second.aggregate ?? "none") === "none") {
    summary[second.key] = `${spaces.length} space(s) : ${data
      .map((row) => row[first.key])
      .join(", ")}`;
  }
  data.push(summary);

  const labels: Record<string, string> = {};
  const columnTypes: Record<string, TableColumnType> = {};
  for (const column of columns) {
    labels[column.key] = column.label;
    if (column.type) columnTypes[column.key] = column.type;
  }

  const usedRoles = columns
    .map((column) => columnRole(column))
    .filter((role): role is AnalyticsFieldRole => role !== null);

  return withWarning(
    {
      labels,
      data,
      columnTypes,
      currency: schema.currencyLabel,
      areaUnit: schema.areaUnitLabel,
    },
    buildSchemaWarning(spaces, details, usedRoles, schema)
  );
}

/**
 * Generate a message content for the quick action result
 */
export function generateQuickActionMessage(
  actionType: "Financial Summary" | "Opportunity Cost" | "Wing Analysis",
  spacesCount: number,
  propertyName?: string,
  /** Overrides the "Wing Analysis" wording (e.g. "Zone Analysis"). */
  wingTitle?: string
): string {
  const property = propertyName ? ` for ${propertyName}` : "";

  if (actionType === "Financial Summary") {
    return `Here's the Financial Summary for ${spacesCount} selected space${spacesCount > 1 ? "s" : ""}${property}. The table shows key financial metrics including Sales R12, Total Rent, Rent per SF, and Occupancy Cost.`;
  } else if (actionType === "Wing Analysis") {
    const title = wingTitle || "Wing Analysis";
    return `Here's the ${title} for ${spacesCount} selected space${spacesCount > 1 ? "s" : ""}${property}. The table compares the selected spaces column by column, with a weighted summary row. The spaces are highlighted on the plan and the highlighted plan can be exported.`;
  } else {
    return `Here's the Opportunity Cost analysis for ${spacesCount} selected space${spacesCount > 1 ? "s" : ""}${property}. This calculates the remaining lease value based on current and future tenant agreements.`;
  }
}

/**
 * Resolve the tenant name and merchandise category for a single selected space
 * (used by the AI-backed "Tenant Ranking" / "Tenant Comparison" quick actions).
 */
export function getTenantContextForSpace(
  space: SpaceInfo,
  options?: QuickActionOptions
): {
  tenantName: string;
  merchandiseCategory: string;
} {
  const schema = resolveAnalyticsSchema(options?.analyticsSchema, options?.planType);
  const tenant = getTenantInfo(space, "current", schema);
  return {
    tenantName: fieldText(tenant, "tenantName", schema) || space.spaceHeader || "",
    merchandiseCategory: fieldText(tenant, "category", schema),
  };
}

/**
 * Build the prompt for the portfolio-wide "Tenant Ranking" quick action.
 */
export function buildTenantRankingPrompt(tenantName: string): string {
  return (
    `List every location of tenant "${tenantName}" across the entire portfolio, ` +
    `ranked by sales per square foot (highest first). For each location include: ` +
    `property name, space number, GLA (sq ft), sales R12, sales per sq ft, and ` +
    `occupancy cost %. Also state the total number of locations of "${tenantName}" ` +
    `across the portfolio. Return the result as a table.`
  );
}

/**
 * Build the prompt for the "Tenant Comparison" quick action (peers in the same
 * merchandise category at the given property).
 */
export function buildTenantComparisonPrompt(
  tenantName: string,
  merchandiseCategory: string,
  propertyName: string
): string {
  const category = merchandiseCategory
    ? ` in the same merchandise category ("${merchandiseCategory}")`
    : " in the same merchandise category";
  const atProperty = propertyName ? ` at property "${propertyName}"` : "";
  return (
    `Compare tenant "${tenantName}"${atProperty} against the other tenants${category}. ` +
    `Exclude from the comparison any tenant whose sales (sales per sq ft or sales R12) ` +
    `is zero, null, or missing — do not include them in the ranking or in the category average. ` +
    `Rank the remaining tenants by sales per square foot (highest first) and show how ` +
    `"${tenantName}" compares to its peers, including the category average and ` +
    `whether "${tenantName}" is above or below it. For each tenant include: ` +
    `space number, tenant name, GLA (sq ft), sales per sq ft, and occupancy cost %. ` +
    `Return the result as a table.`
  );
}
